const DIRECTIONS = [
  { bit: 1, opposite: 4, dr: -1, dc: 0 },
  { bit: 2, opposite: 8, dr: 0, dc: 1 },
  { bit: 4, opposite: 1, dr: 1, dc: 0 },
  { bit: 8, opposite: 2, dr: 0, dc: -1 }
]

function rotateMask(mask, turns = 1) {
  let rotated = mask
  for (let i = 0; i < turns; i++) {
    rotated = ((rotated << 1) & 15) | ((rotated >> 3) & 1)
  }
  return rotated
}

function connectionCount(mask) {
  let value = mask
  let count = 0
  while (value > 0) {
    count += value & 1
    value >>= 1
  }
  return count
}

class CircuitGame {
  constructor(options = {}) {
    this.level = Math.max(1, Math.floor(options.level || 1))
    const defaultSize = this.level <= 2 ? 5 : this.level <= 5 ? 6 : 7
    this.rows = Math.max(4, Math.min(8, Math.floor(options.rows || defaultSize)))
    this.cols = Math.max(4, Math.min(8, Math.floor(options.cols || defaultSize)))
    this.timeLimit = Math.max(
      30,
      Math.floor(options.timeLimit || 65 + this.rows * this.cols * 1.6)
    )
    this.seed = Number.isFinite(options.seed) ? Math.floor(options.seed) : Date.now()
    this.randomState = this.seed % 2147483647
    if (this.randomState <= 0) this.randomState += 2147483646
    this.moves = 0
    this.status = 'ready'
    this.startedAt = null
    this.finishedAt = null
    this.score = 0
    this.stars = 0
    this.source = {
      r: Math.floor(this.rows / 2),
      c: Math.floor(this.cols / 2)
    }
    this.board = this.createPuzzle()
    this.powered = new Set()
    this.updatePower()
  }

  random() {
    this.randomState = (this.randomState * 16807) % 2147483647
    return (this.randomState - 1) / 2147483646
  }

  randomIndex(length) {
    return Math.floor(this.random() * length)
  }

  createEmptyBoard() {
    return Array.from({ length: this.rows }, () =>
      Array.from({ length: this.cols }, () => ({
        mask: 0,
        solutionMask: 0,
        terminal: false
      }))
    )
  }

  inBounds(r, c) {
    return r >= 0 && r < this.rows && c >= 0 && c < this.cols
  }

  cellKey(r, c) {
    return `${r},${c}`
  }

  createSolvedTree() {
    const board = this.createEmptyBoard()
    const visited = new Set([this.cellKey(this.source.r, this.source.c)])
    const frontier = []

    const addFrontier = (r, c) => {
      for (const direction of DIRECTIONS) {
        const nr = r + direction.dr
        const nc = c + direction.dc
        if (!this.inBounds(nr, nc) || visited.has(this.cellKey(nr, nc))) continue
        frontier.push({ r, c, nr, nc, direction })
      }
    }

    addFrontier(this.source.r, this.source.c)
    while (frontier.length > 0) {
      const index = this.randomIndex(frontier.length)
      const edge = frontier[index]
      frontier[index] = frontier[frontier.length - 1]
      frontier.pop()
      const nextKey = this.cellKey(edge.nr, edge.nc)
      if (visited.has(nextKey)) continue

      board[edge.r][edge.c].solutionMask |= edge.direction.bit
      board[edge.nr][edge.nc].solutionMask |= edge.direction.opposite
      visited.add(nextKey)
      addFrontier(edge.nr, edge.nc)
    }

    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        const cell = board[r][c]
        cell.terminal =
          connectionCount(cell.solutionMask) === 1 &&
          (r !== this.source.r || c !== this.source.c)
      }
    }
    return board
  }

  minimumTurns(mask, targetMask) {
    for (let turns = 0; turns < 4; turns++) {
      if (rotateMask(mask, turns) === targetMask) return turns
    }
    return 0
  }

  scrambleBoard(board) {
    let attempts = 0
    do {
      this.parMoves = 0
      for (let r = 0; r < this.rows; r++) {
        for (let c = 0; c < this.cols; c++) {
          const cell = board[r][c]
          cell.mask = rotateMask(cell.solutionMask, this.randomIndex(4))
          this.parMoves += this.minimumTurns(cell.mask, cell.solutionMask)
        }
      }
      attempts += 1
      this.board = board
      this.updatePower()
    } while ((this.isComplete() || this.parMoves < this.rows * this.cols * 0.45) && attempts < 40)
  }

  createPuzzle() {
    let board = null
    for (let attempt = 0; attempt < 30; attempt++) {
      board = this.createSolvedTree()
      const terminals = board.flat().filter((cell) => cell.terminal).length
      if (terminals >= 3) break
    }
    this.scrambleBoard(board)
    return board
  }

  start(now = Date.now()) {
    if (this.status !== 'ready') return this.getState(now)
    this.status = 'playing'
    this.startedAt = now
    return this.getState(now)
  }

  remainingSeconds(now = Date.now()) {
    if (this.startedAt === null) return this.timeLimit
    const endTime = this.finishedAt ?? now
    return Math.max(0, Math.ceil(this.timeLimit - (endTime - this.startedAt) / 1000))
  }

  tick(now = Date.now()) {
    if (this.status === 'playing' && this.remainingSeconds(now) <= 0) {
      this.status = 'lost'
      this.finishedAt = now
      this.score = 0
      this.stars = 0
    }
    return this.status
  }

  updatePower() {
    const powered = new Set()
    const queue = [{ ...this.source }]
    powered.add(this.cellKey(this.source.r, this.source.c))

    for (let index = 0; index < queue.length; index++) {
      const current = queue[index]
      const cell = this.board[current.r][current.c]
      for (const direction of DIRECTIONS) {
        if ((cell.mask & direction.bit) === 0) continue
        const nr = current.r + direction.dr
        const nc = current.c + direction.dc
        if (!this.inBounds(nr, nc)) continue
        const neighbor = this.board[nr][nc]
        if ((neighbor.mask & direction.opposite) === 0) continue
        const key = this.cellKey(nr, nc)
        if (powered.has(key)) continue
        powered.add(key)
        queue.push({ r: nr, c: nc })
      }
    }
    this.powered = powered
    return powered
  }

  isComplete() {
    let terminalCount = 0
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        if (!this.board[r][c].terminal) continue
        terminalCount += 1
        if (!this.powered.has(this.cellKey(r, c))) return false
      }
    }
    return terminalCount > 0
  }

  finishWin(now) {
    this.status = 'won'
    this.finishedAt = now
    const remaining = this.remainingSeconds(now)
    const efficiencyBonus = Math.max(0, this.parMoves * 2 - this.moves) * 18
    this.score = 1000 + remaining * 12 + efficiencyBonus + this.level * 100
    const margin = Math.ceil(this.rows * this.cols * 0.3)
    this.stars = this.moves <= this.parMoves ? 3 : this.moves <= this.parMoves + margin ? 2 : 1
  }

  rotate(r, c, now = Date.now()) {
    this.tick(now)
    if (this.status !== 'playing') return { ok: false, reason: 'not_playing', state: this.getState(now) }
    if (!this.inBounds(r, c)) return { ok: false, reason: 'out_of_bounds', state: this.getState(now) }

    const cell = this.board[r][c]
    if (cell.mask === 15) return { ok: false, reason: 'fixed', state: this.getState(now) }
    cell.mask = rotateMask(cell.mask)
    this.moves += 1
    this.updatePower()
    if (this.isComplete()) this.finishWin(now)
    return { ok: true, r, c, state: this.getState(now) }
  }

  getState(now = Date.now()) {
    this.tick(now)
    let poweredTerminals = 0
    let terminalCount = 0
    const board = this.board.map((row, r) =>
      row.map((cell, c) => {
        const powered = this.powered.has(this.cellKey(r, c))
        if (cell.terminal) {
          terminalCount += 1
          if (powered) poweredTerminals += 1
        }
        return {
          mask: cell.mask,
          terminal: cell.terminal,
          source: r === this.source.r && c === this.source.c,
          powered
        }
      })
    )
    return {
      level: this.level,
      rows: this.rows,
      cols: this.cols,
      status: this.status,
      moves: this.moves,
      parMoves: this.parMoves,
      timeLimit: this.timeLimit,
      remainingSeconds: this.remainingSeconds(now),
      terminalCount,
      poweredTerminals,
      score: this.score,
      stars: this.stars,
      board
    }
  }
}

module.exports = { CircuitGame, DIRECTIONS, rotateMask, connectionCount }
